import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    ClientReviewSchema,
    ClientReviewRequestSchema,
    ReviewSubmissionResponseSchema,
    ReviewUpdateResponseSchema,
} from "../schemas/reviews-v2";
import type {
    ClientReviewsQueryParams,
    ClientReviewRequestsQueryParams,
    SubmitReviewByRequestIdBody,
    SubmitReviewByExpertIdBody,
    SubmitReviewByTokenBody,
    UpdateReviewBody,
} from "../schemas/reviews-v2";

/**
 * Creates the Client Reviews V2 API endpoints.
 * 
 * These endpoints require authentication as a Client user.
 * 
 * @see V2_REVIEWS_CLIENT_API.md for full API documentation
 */
export function createClientReviewsV2Api(http: HttpClient, baseUrl: string) {
    const clientBaseUrl = `${baseUrl}/client/reviews-v2`;
    const reviewRequestsBaseUrl = `${baseUrl}/client/review-requests-v2`;
    
    return {
        /**
         * Get all reviews submitted by the authenticated client.
         * 
         * GET /api/v2/client/reviews-v2
         * 
         * @param query.limit - Number of results per page (default: 15, max: 100)
         * @param query.cursor - Pagination cursor from previous response
         */
        getReviews: createEndpointWithPagination<
            z.infer<typeof ClientReviewSchema>,
            unknown,
            unknown,
            ClientReviewsQueryParams
        >(http, "GET", "/", {
            base: clientBaseUrl,
            itemSchema: ClientReviewSchema,
        }),

        /**
         * Submit a review in response to a review request.
         * 
         * POST /api/v2/client/reviews-v2
         * 
         * The system automatically fetches expert_id, project_id, and project_value_range_id
         * from the review request. Any manually submitted values for these fields will be ignored.
         * 
         * @param body.request_id - ID of the review request to respond to
         * @param body.quality - Quality rating (1-5)
         * @param body.communication - Communication rating (1-5)
         * @param body.timelines - Timelines rating (1-5)
         * @param body.value_for_money - Value for money rating (1-5)
         * @param body.review_text - Review text (min 10 characters)
         * @param body.is_anonymous - Hide identity from expert/public (default: false)
         */
        submitReviewByRequestId: createEndpoint<
            z.infer<typeof ReviewSubmissionResponseSchema>,
            SubmitReviewByRequestIdBody,
            QueryParams
        >(http, "POST", "/", {
            base: clientBaseUrl,
            responseSchema: ReviewSubmissionResponseSchema,
        }),

        /**
         * Submit an organic review directly to an expert.
         * 
         * POST /api/v2/client/reviews-v2
         * 
         * For reviews not initiated by a review request.
         * 
         * @param body.expert_id - ID of expert being reviewed
         * @param body.project_id - Optional project ID
         * @param body.quality - Quality rating (1-5)
         * @param body.communication - Communication rating (1-5)
         * @param body.timelines - Timelines rating (1-5)
         * @param body.value_for_money - Value for money rating (1-5)
         * @param body.review_text - Review text (min 10 characters)
         * @param body.is_anonymous - Hide identity from expert/public (default: false)
         * @param body.project_value_range_id - Optional project value range ID
         * @param body.idempotency_key - Stable key required for duplicate-safe direct submissions
         */
        submitReviewByExpertId: createEndpoint<
            z.infer<typeof ReviewSubmissionResponseSchema>,
            SubmitReviewByExpertIdBody,
            QueryParams
        >(http, "POST", "/", {
            base: clientBaseUrl,
            responseSchema: ReviewSubmissionResponseSchema,
        }),

        /**
         * Submit a review using a token (for clients who prefer using the review link when logged in).
         * 
         * POST /api/v2/client/reviews-v2
         * 
         * @param body.token - Review invitation token
         * @param body.quality - Quality rating (1-5)
         * @param body.communication - Communication rating (1-5)
         * @param body.timelines - Timelines rating (1-5)
         * @param body.value_for_money - Value for money rating (1-5)
         * @param body.review_text - Review text (min 10 characters)
         * @param body.is_anonymous - Hide identity from expert/public (default: false)
         * @param body.project_value_range_id - Optional project value range ID
         */
        submitReviewByToken: createEndpoint<
            z.infer<typeof ReviewSubmissionResponseSchema>,
            SubmitReviewByTokenBody,
            QueryParams
        >(http, "POST", "/", {
            base: clientBaseUrl,
            responseSchema: ReviewSubmissionResponseSchema,
        }),

        /**
         * Update an existing review. Creates a revision of the previous version.
         * 
         * PUT /api/v2/client/reviews-v2/{review}
         * 
         * At least one field must be provided for update.
         * 
         * @param params.review - The ID of the review to update
         * @param body.quality - Quality rating (1-5)
         * @param body.communication - Communication rating (1-5)
         * @param body.timelines - Timelines rating (1-5)
         * @param body.value_for_money - Value for money rating (1-5)
         * @param body.review_text - Review text (min 10 characters)
         * @param body.project_value_range_id - Project value range ID
         */
        updateReview: createEndpoint<
            z.infer<typeof ReviewUpdateResponseSchema>,
            UpdateReviewBody,
            QueryParams,
            { review: number }
        >(http, "PUT", "/:review", {
            base: clientBaseUrl,
            responseSchema: ReviewUpdateResponseSchema,
            params: ["review"],
        }),

        /**
         * Get all review requests received by the authenticated client.
         * 
         * GET /api/v2/client/review-requests-v2
         * 
         * @param query.limit - Number of results per page (default: 15, max: 100)
         * @param query.cursor - Pagination cursor from previous response
         * @param query.status - Filter by status: "pending", "completed", "expired"
         */
        getReviewRequests: createEndpointWithPagination<
            z.infer<typeof ClientReviewRequestSchema>,
            unknown,
            unknown,
            ClientReviewRequestsQueryParams
        >(http, "GET", "/", {
            base: reviewRequestsBaseUrl,
            itemSchema: ClientReviewRequestSchema,
        }),
    };
}

export type ClientReviewsV2Api = ReturnType<typeof createClientReviewsV2Api>;
